/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: FirewallRuleModal.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Modal component for creating/editing firewall rules
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, ReactNode } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import Modal from '../../../components/ui/Modal';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select-shadcn';
import { Toggle } from '../../../components/ui/Toggle';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { createFirewallRule, updateFirewallRule, fetchFirewallServices, applyFirewallRules } from '../api/firewallRulesApi';
import type { FirewallRule, FirewallRuleFormData, FirewallService } from '../types';

type FormFieldProps = {
  id: string;
  label: string;
  tooltip: string;
  children: ReactNode;
  required?: boolean;
};

const FormField = ({ id, label, tooltip, children, required }: FormFieldProps) => (
  <div className="flex items-center gap-4">
    <div className="w-36 flex-shrink-0 h-10 flex items-center">
      {tooltip ? (
        <SimpleTooltip content={tooltip} position="top">
          <Label htmlFor={id} className="text-sm font-medium text-foreground cursor-help flex items-center gap-1">
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
          </Label>
        </SimpleTooltip>
      ) : (
        <Label htmlFor={id} className="text-sm font-medium text-foreground flex items-center gap-1">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
      )}
    </div>
    <div className="flex-1">{children}</div>
  </div>
);

interface FirewallRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  rule?: FirewallRule | null;
}

export function FirewallRuleModal({
  isOpen,
  onClose,
  onSuccess,
  rule,
}: FirewallRuleModalProps) {
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState<FirewallService[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [formData, setFormData] = useState<FirewallRuleFormData>({
    name: '',
    description: '',
    action: 'accept',
    direction: 'input',
    priority: 0,
    serviceId: 0, // Will be set when user selects a service
    sourceAddress: '',
    destinationAddress: '',
    sourcePort: '',
    destinationPort: '',
    interfaceName: '',
    enabled: true,
  });

  // Load services when modal opens
  useEffect(() => {
    if (isOpen) {
      setLoadingServices(true);
      fetchFirewallServices()
        .then((servicesList) => {
          setServices(servicesList);
        })
        .catch((error) => {
          console.error('Error loading firewall services:', error);
          setServices([]);
        })
        .finally(() => setLoadingServices(false));
    } else {
      // Reset services when modal closes
      setServices([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (rule) {
      setFormData({
        name: rule.name,
        description: rule.description || '',
        action: rule.action,
        direction: rule.direction,
        priority: rule.priority,
        serviceId: rule.serviceId,
        sourceAddress: rule.sourceAddress || '',
        destinationAddress: rule.destinationAddress || '',
        sourcePort: rule.sourcePort || '',
        destinationPort: rule.destinationPort || '',
        interfaceName: rule.interfaceName || '',
        enabled: rule.enabled,
      });
    } else {
      // Reset form but keep serviceId as undefined (will be required)
      setFormData({
        name: '',
        description: '',
        action: 'accept',
        direction: 'input',
        priority: 0,
        serviceId: 0, // Will be required, must be selected
        sourceAddress: '',
        destinationAddress: '',
        sourcePort: '',
        destinationPort: '',
        interfaceName: '',
        enabled: true,
      });
    }
  }, [rule, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.serviceId || formData.serviceId === 0) {
      showError(t('firewallRules.messages.serviceRequired'));
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        serviceId: formData.serviceId,
        sourceAddress: formData.sourceAddress || null,
        destinationAddress: formData.destinationAddress || null,
        sourcePort: formData.sourcePort || null,
        destinationPort: formData.destinationPort || null,
        interfaceName: formData.interfaceName || null,
        description: formData.description || null,
      };

      if (rule) {
        await updateFirewallRule(rule.id, payload);
        showSuccess(t('firewallRules.messages.updateSuccess'));
      } else {
        await createFirewallRule(payload);
        showSuccess(t('firewallRules.messages.createSuccess'));
      }

      // Apply changes to the system firewall
      try {
        await applyFirewallRules();
        showSuccess(t('firewallRules.messages.appliedToFirewall'));
      } catch (err) {
        // Show warning but don't fail the save operation
        showError(t('firewallRules.messages.applyError') + ': ' + (err instanceof Error ? err.message : ''));
      }

      onSuccess();
      onClose();
    } catch (error) {
      showError(error instanceof Error ? error.message : t('firewallRules.messages.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={rule ? t('firewallRules.modal.editTitle') : t('firewallRules.modal.createTitle')}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Línea 1: Name */}
        <FormField
          id="name"
          label={t('firewallRules.form.name')}
          tooltip={t('firewallRules.tooltips.name')}
          required
        >
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder={t('firewallRules.form.namePlaceholder')}
            required
            disabled={loading}
          />
        </FormField>

        {/* Línea 2: Action */}
        <FormField
          id="action"
          label={t('firewallRules.form.action')}
          tooltip={t('firewallRules.tooltips.action')}
          required
        >
          <Select
            value={formData.action}
            onValueChange={(value: FirewallRuleFormData['action']) =>
              setFormData({ ...formData, action: value })
            }
          >
            <SelectTrigger className="w-full" disabled={loading}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="accept">{t('firewallRules.actions.accept')}</SelectItem>
              <SelectItem value="drop">{t('firewallRules.actions.drop')}</SelectItem>
              <SelectItem value="reject">{t('firewallRules.actions.reject')}</SelectItem>
            </SelectContent>
          </Select>
        </FormField>

        {/* Línea 3: Direction */}
        <FormField
          id="direction"
          label={t('firewallRules.form.direction')}
          tooltip={t('firewallRules.tooltips.direction')}
          required
        >
          <Select
            value={formData.direction}
            onValueChange={(value: FirewallRuleFormData['direction']) =>
              setFormData({ ...formData, direction: value })
            }
          >
            <SelectTrigger className="w-full" disabled={loading}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="input">{t('firewallRules.directions.input')}</SelectItem>
              <SelectItem value="output">{t('firewallRules.directions.output')}</SelectItem>
              <SelectItem value="forward">{t('firewallRules.directions.forward')}</SelectItem>
            </SelectContent>
          </Select>
        </FormField>

        {/* Línea 4: Service */}
        <FormField
          id="service"
          label={t('firewallRules.form.service')}
          tooltip={t('firewallRules.tooltips.service')}
          required
        >
          <Select
            value={formData.serviceId && formData.serviceId > 0 ? formData.serviceId.toString() : ''}
            onValueChange={(value) =>
              setFormData({ ...formData, serviceId: value ? parseInt(value, 10) : 0 })
            }
          >
            <SelectTrigger className="w-full" disabled={loading || loadingServices}>
              <SelectValue placeholder={t('firewallRules.form.servicePlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              {loadingServices ? (
                <SelectItem value="__loading__">
                  {t('common.loading')}
                </SelectItem>
              ) : services.length === 0 ? (
                <SelectItem value="__empty__">
                  {t('firewallRules.form.noServicesAvailable')}
                </SelectItem>
              ) : (
                services.map((service) => (
                  <SelectItem key={service.id} value={service.id.toString()}>
                    {service.name} ({service.protocol}/{service.port})
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </FormField>

        {/* Línea 5: Priority */}
        <FormField
          id="priority"
          label={t('firewallRules.form.priority')}
          tooltip={t('firewallRules.tooltips.priority')}
        >
          <Input
            id="priority"
            type="number"
            min="0"
            max="9999"
            value={formData.priority?.toString() || '0'}
            onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value, 10) || 0 })}
            placeholder="0"
            disabled={loading}
            className="font-mono"
          />
        </FormField>

        {/* Línea 6: Source Address */}
        <FormField
          id="sourceAddress"
          label={t('firewallRules.form.sourceAddress')}
          tooltip={t('firewallRules.tooltips.sourceAddress')}
        >
          <Input
            id="sourceAddress"
            value={formData.sourceAddress || ''}
            onChange={(e) => setFormData({ ...formData, sourceAddress: e.target.value })}
            placeholder={t('firewallRules.form.sourceAddressPlaceholder')}
            disabled={loading}
            className="font-mono"
          />
        </FormField>

        {/* Línea 7: Destination Address */}
        <FormField
          id="destinationAddress"
          label={t('firewallRules.form.destinationAddress')}
          tooltip={t('firewallRules.tooltips.destinationAddress')}
        >
          <Input
            id="destinationAddress"
            value={formData.destinationAddress || ''}
            onChange={(e) => setFormData({ ...formData, destinationAddress: e.target.value })}
            placeholder={t('firewallRules.form.destinationAddressPlaceholder')}
            disabled={loading}
            className="font-mono"
          />
        </FormField>

        {/* Línea 8: Interface */}
        <FormField
          id="interfaceName"
          label={t('firewallRules.form.interfaceName')}
          tooltip={t('firewallRules.tooltips.interfaceName')}
        >
          <Input
            id="interfaceName"
            value={formData.interfaceName || ''}
            onChange={(e) => setFormData({ ...formData, interfaceName: e.target.value })}
            placeholder={t('firewallRules.form.interfaceNamePlaceholder')}
            disabled={loading}
            className="font-mono"
          />
        </FormField>

        {/* Línea 9: Enabled */}
        <FormField
          id="enabled"
          label={t('firewallRules.form.enabled')}
          tooltip={t('firewallRules.tooltips.enabled')}
        >
          <Toggle
            value={formData.enabled ?? true}
            onChange={(enabled) => setFormData({ ...formData, enabled })}
            label=""
            disabled={loading}
            trueLabel={t('common.yes')}
            falseLabel={t('common.no')}
          />
        </FormField>

        <div className="flex justify-end gap-2 pt-4 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={loading}>
            {loading
              ? t('common.saving')
              : rule
                ? t('common.update')
                : t('common.create')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
